import { NodeAPI } from '@platform/shared/rpc/node';
import { TabsStore, UserStore } from '@platform/store';
import config from '@platform/shared/config';
import type { NavigationGuardWithThis, RouteComponent, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

const defaultRouteParams: Record<string, Record<string, any>> = {
    ProjectPassportView: { tab: 'general' },
};

function getTabKey(to: RouteLocationNormalized) {
    switch (to.name) {
        case 'ProjectPassportView':
            return `/project/${to.params?.id}`;
        case 'InvoiceFromSupplierView':
            return to.path;
        case 'RequestForSupply':
            return to.path;
        case 'CommercialOfferPage':
            return to.name;
        default: {
            const reserved =
                (to.meta?.tabKey as string | undefined) ??
                (to.query.tabKey && (Array.isArray(to.query.tabKey) ? to.query.tabKey[0] : to.query.tabKey));
            return reserved || to.fullPath;
        }
    }
}

export const routerHook: NavigationGuardWithThis<undefined> = (to, from, next) => {
    if (to.hash.length) {
        return next(to.hash.substring(1));
    }
    if (
        to.name !== 'DynamicTab' &&
        to.name !== '404NotFound' &&
        to.fullPath !== '/home' &&
        to.fullPath !== '/' &&
        !to.fullPath.startsWith('/auth') &&
        !to.meta.public
        // !to.meta?.isDynamicTab
    ) {
        TabsStore.addTabIfNotExists(
            {
                key: getTabKey(to),
                fullPath: to.fullPath,
                path: to.path,
                label: to.meta.label as string,
                cname: to.matched[to.matched.length - 1]?.components?.default?.name,
            },
            true,
            true
        );
    } else TabsStore.setSelectedTab(null);
    if (
        to.fullPath.startsWith('/auth') &&
        UserStore.userState.user !== null &&
        !to.fullPath.startsWith('/auth/password-register')
    ) {
        return next('/');
    }
    next();
    document.title = `${to.meta.label ? to.meta.label + ' | ' : ''}${config.serviceName}`;
};

type RouteData = {
    id: string;
    path: string;
    component: string;
    is_group: boolean;
    group_icon?: string;
    icon?: string;
    name?: string;
    menu: boolean;
    props?: object;
    parent_id?: string;
    route_name?: string;
    menu_link_parameters?: Record<string, any>;
};
export type RouteMeta = Pick<RouteData, 'menu' | 'icon'> & {
    label?: string;
    group?: string;
    groupIcon?: string;
    subGroup?: string;
    subGroupIcon?: string;
    roles?: unknown[];
    rawProps?: any;
    menuLinkParameters?: Record<string, any>;
    componentName?: string;
};
type Lazy<T> = () => Promise<T>;

export const fetchAndBuildRoutes = async (
    findComponentByName: (name: string) => RouteComponent | Lazy<RouteComponent>,
    prependRoutes?: RouteRecordRaw[],
    appendRoutes?: RouteRecordRaw[]
) => {
    const routesFromQuery: RouteRecordRaw[] = [...(prependRoutes || [])];
    const lifetime = localStorage.getItem(config.lifetimeToken);
    if (UserStore.isLoggedIn.value) {
        const routeData: RouteData[] = (await NodeAPI.AuthService.getRoutesMenu()) as RouteData[];
        if (routeData?.length) {
            const routesMap = new Map<string, RouteData>(routeData.map((data) => [data.id, data]));
            for (const route of routeData) {
                if (route.path === '/' || route.path === 'home' || route.is_group) continue;
                // TODO: enable route names
                const routeRecord: RouteRecordRaw = {
                    name: route.route_name || route.path, // route.route_name || `id:${route.id}`,
                    path: route.path.startsWith('/') ? route.path : '/' + route.path,
                    component: findComponentByName(route.component),
                    props: (r) => {
                        const { tabKey, ...queryParams } = r.query;
                        return {
                            ...r.params,
                            ...queryParams,
                            ...(route.props || {}),
                        };
                    },
                };
                if (typeof routeRecord.name === 'string') {
                    const defaultParams = defaultRouteParams[routeRecord.name];
                    if (defaultParams) {
                        routeRecord.beforeEnter = (r, _, next) => {
                            const result = {
                                ...r,
                                params: {
                                    ...r.params,
                                },
                            };
                            let success = false;
                            for (const [k, v] of Object.entries(defaultParams)) {
                                if (!result.params[k] && v) {
                                    result.params[k] = v;
                                    success = true;
                                }
                            }
                            return success ? next(result) : next();
                        };
                    }
                }
                const routeMeta: RouteMeta = {
                    label: route.name,
                    menu: route.menu,
                    icon: route.icon,
                    rawProps: route.props,
                    menuLinkParameters: route.menu_link_parameters,
                    componentName: route.component,
                };
                if (route.parent_id) fillRouteGroup(routesMap, route.parent_id, routeMeta);
                routeRecord.meta = routeMeta;
                if (routesFromQuery[0]?.children) {
                    routesFromQuery[0].children?.push(routeRecord);
                } else routesFromQuery.push(routeRecord);
            }
        }
    }
    if (process.env.MODE !== 'ssr' && appendRoutes?.length) {
        routesFromQuery.push(...appendRoutes);
    }
    return routesFromQuery;
};

function fillRouteGroup(routesMap: Map<string, RouteData>, id: string, routeMeta: RouteMeta, subGroup = false) {
    const item = routesMap.get(id);
    if (!item || !item.is_group) return;
    routeMeta.group = item.name;
    routeMeta.groupIcon = item.icon;
    if (!subGroup && item.parent_id) {
        routeMeta.subGroup = item.name;
        routeMeta.subGroupIcon = item.icon;
        fillRouteGroup(routesMap, item.parent_id, routeMeta, true);
    }
}
